/**
 * Phase 1.5.4 拡張 — 日時変更（reschedule）完了画面。
 *
 * step5Confirm から API 成功後に呼ばれ、変更前後の日時を表示して
 * 「予約確認画面に戻る」「トーク画面に戻る」のボタンを提供する。
 */

import { setRoot } from "../render";

export interface RescheduleDoneInput {
  oldDate: string;
  oldTime: string;
  newDate: string;
  newTime: string;
  contact_method: "zoom" | "line_call";
  /** 新規 Calendar 作成成功時に取得できる新 Meet URL（zoom かつ作成成功時のみ non-null） */
  meetUrl: string | null;
}

export function renderRescheduleDone(input: RescheduleDoneInput): void {
  const contactLabel =
    input.contact_method === "zoom" ? "オンライン面談" : "LINE電話";

  // zoom の場合のみ Meet URL ブロック表示（作成成功時）
  const meetUrlBlock =
    input.contact_method === "zoom" && input.meetUrl
      ? `
        <div class="mt-4 bg-blue-50 border border-blue-200 rounded p-3 text-sm">
          <div class="text-xs text-gray-600 mb-1">オンラインミーティング URL</div>
          <a href="${escapeHtml(input.meetUrl)}" target="_blank" rel="noopener noreferrer"
            class="text-blue-700 underline break-all">
            ${escapeHtml(input.meetUrl)}
          </a>
        </div>
      `
      : "";

  setRoot(`
    <div class="text-center pt-12 pb-8 px-2">
      <div class="text-4xl mb-4">✅</div>
      <h1 class="text-xl font-bold text-gray-900 mb-6">
        日時を変更しました
      </h1>

      <div class="bg-gray-50 rounded-lg p-3 text-sm leading-7 text-left max-w-xs mx-auto">
        <div class="text-gray-500 text-xs mb-1">📌 変更前</div>
        <div class="text-gray-700">
          ${escapeHtml(formatJpDate(input.oldDate))} ${escapeHtml(input.oldTime)}
        </div>
        <div class="my-2 border-t border-dashed border-gray-300"></div>
        <div class="text-gray-500 text-xs mb-1">✅ 新しい日時</div>
        <div class="font-bold text-emerald-700 text-base">
          ${escapeHtml(formatJpDate(input.newDate))} ${escapeHtml(input.newTime)}
        </div>
        <div class="text-xs text-gray-600 mt-2">${escapeHtml(contactLabel)}</div>
      </div>

      ${meetUrlBlock}

      <div class="mt-6 text-base text-gray-800 leading-relaxed space-y-1 mb-6">
        <p>ご連絡ありがとうございます😊</p>
        <p>新しい日時でお待ちしています^^</p>
      </div>

      <div class="flex flex-col gap-2 max-w-xs mx-auto">
        <button id="btn-go-my-reservations"
          class="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold">
          📋 予約確認画面に戻る
        </button>
        <button id="btn-close-liff"
          class="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg">
          トーク画面に戻る
        </button>
      </div>
    </div>
  `);

  document.getElementById("btn-go-my-reservations")?.addEventListener("click", async () => {
    const { renderMyReservations } = await import("./myReservations");
    await renderMyReservations();
  });

  document.getElementById("btn-close-liff")?.addEventListener("click", () => {
    const liff = (window as any).liff;
    if (liff && typeof liff.closeWindow === "function") {
      try {
        liff.closeWindow();
      } catch {
        // ignore
      }
    }
  });
}

function formatJpDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const w = ["日", "月", "火", "水", "木", "金", "土"][dt.getDay()];
  return `${m}月${d}日(${w})`;
}

function escapeHtml(s: unknown): string {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
